import * as React from "react";
import { icons as lucide } from "lucide-react";

import { CandyGlimmer } from "./CandyGlimmer";
import AdhityaaProfilePicture from "../../images/adtac_profile_picture.jpg";
import VideoThumbnail from "../../images/blog_grpc_debugging_video_thumbnail.png";

export const Post_250610_GRPCDebugging = () => {
  React.useEffect(() => {
    const orig = document.title;
    document.title = "gRPC debugging sucks so we fixed it | Subtrace";
    return () => {
      document.title = orig;
    };
  }, []);

  return (
    <article className="w-full flex flex-col items-start px-[8px] sm:px-[36px] md:px-[72px] pt-12 sm:pt-24 pb-16 text-zinc-300/90">
      <h2 className="w-full flex flex-col space-y-3">
        <CandyGlimmer text={"// launch"} />
        <span className="leading-[1.50] font-bold max-w-[28rem] xl:max-w-[32rem] text-[24px] xl:text-[28px] text-zinc-300">gRPC debugging sucks so we fixed it</span>
      </h2>

      <div className="w-full h-[24px]" />

      <a href="https://x.com/adtac_" target="_blank" className="text-[12px] text-zinc-700 hover:text-zinc-600 font-medium flex items-center space-x-3 hover:brightness-[1.10]">
        <img src={AdhityaaProfilePicture} className="w-[24px] h-[24px] rounded-sm" />
        <div className="flex flex-col space-y-1 leading-[1]">
          <div>Adhityaa Chandrasekar</div>
          <div className="text-zinc-700/50">Sep 29, 2025</div>
        </div>
      </a>

      <div className="w-full h-[48px]" />

      <div className="w-full flex flex-col lg:flex-row relative">
        <div className="z-[0] md:max-w-[560px] lg:max-w-[480px] lg:mr-4 xl:mr-8 flex flex-col text-[16px] leading-[1.70]">
          <div className="w-full aspect-[16/9] bg-zinc-700/15 border-t border-zinc-700/50 rounded-[6px] overflow-hidden relative shadow-inner shadow-[0_30px_60px_-60px_rgba(255,255,255,0.2)]">
            <a
              href="/login"
              className="active:scale-[0.90] group p-2 -m-2 text-zinc-600 w-fit text-zinc-500/50 text-[10px] font-mono font-medium absolute top-[24px] left-[24px] leading-[1.40] font-medium cursor-pointer hover:opacity-[1.00] opacity-[0.30]"
            >
              <span className="leading-[12px]">OPEN SUBTRACE</span>
              <lucide.ArrowRight size={11} strokeWidth={4} className="mx-2 hidden group-hover:inline opacity-[0.25] text-zinc-500" />
            </a>

            <div className="text-zinc-600 w-fit text-zinc-500/50 text-[12px] font-medium absolute bottom-[24px] left-[24px] leading-[1.40]">
              <span className="opacity-[1.00] font-medium select-none">Context&mdash;&nbsp;&nbsp;</span>
              <span className="opacity-[0.50]">We're building the Heroku for tracing. </span>
              <br />
              <span className="opacity-[0.50]">You can find </span>
              <lucide.MoveUpRight size={9} strokeWidth={4.5} className="opacity-[1.00] inline" />
              <span className="opacity-[1.00]"> more </span>
              <span className="opacity-[1.00]">perf bottlenecks </span>
              <span className="opacity-[0.50]">in </span>
              <lucide.MoveDownRight size={9} strokeWidth={4.5} className="opacity-[1.00] inline" />
              <span className="opacity-[1.00]"> less </span>
              <span className="opacity-[1.00]">time. </span>
              <br />
              <span className="opacity-[0.50]">Subtrace now supports gRPC. </span>
            </div>
          </div>

          <div className="w-full h-[24px]" />

          <div className="w-full h-[24px]" />

          <div className="flex flex-col">
            <div className="flex flex-col">
              <p>
                <span>Here's a dirty secret: Google strategically open sourced protobuf and gRPC but none of the magical internal tooling that’s supposed to go with it.</span>{" "}
                <span>They have an internal tool called RPCStudio that lets you see every RPC flowing through prod services in realtime.</span>{" "}
                <span>It made me way more productive as a engineer on the gRPC-Go team at Google but nobody outside even knows it exists.</span>
                <div className="relative group lg:p-2 lg:-m-2 transition-filter delay-[75ms] duration-[100ms] ease-in-out lg:hover:brightness-[1.20]">
                  <aside className="w-fit lg:w-full max-w-[360px] lg:max-w-[min(100%,400px)] lg:absolute lg:left-[calc(100%+50px)] lg:bottom-[calc(100%+16px)] text-[13px] text-zinc-500/40 px-[32px] py-[20px] px-4 py-2 lg:py-0 rounded-lg border border-zinc-900 bg-zinc-900/25 lg:border-[0px] lg:bg-transparent my-8 lg:my-0">
                    <div className="lg:hidden text-zinc-600/50 font-mono text-[10px] mb-2 select-none">&lt;aside&gt;</div>
                    <span>This</span>{" "}
                    <a
                      href="http://teamblind.com/post/Best-engineering-practices-in-Google-yiLaz5cY"
                      className="border-b border-zinc-700/50 hover:border-zinc-700 px-[1px] mx-[-1px] pb-[1px] mb-[-1px] text-zinc-500/50 hover:brightness-[1.10]"
                      target="_blank"
                      rel="noreferrer nofollow"
                    >
                      Blind post
                    </a>{" "}
                    <span>is the only other reference to RPCStudio I could find on the public internet.</span>
                  </aside>
                  <div className="hidden lg:block transition-all duration-[100ms] ease-out absolute left-[calc(100%+60px)] group-hover:left-[calc(100%+45px)] bottom-[calc(100%+2px)] h-[10px] w-[30px] group-hover:w-[45px] rounded-br-lg border-r-[2px] border-b-[2px] border-zinc-800/70" />
                </div>
              </p>

              <div className="w-full h-[24px]" />

              <p>
                <span>Why don't you have this?</span>{" "}
                <span>
                  Google can force its ~100k engineers to write everything in the same four blessed languages, use the same RPC framework, check all code into the same monorepo,
                  deploy to the same platform, and so on. They've spent literal billions building world-class internal tooling around this.
                </span>{" "}
                <span>They also have Jeff Dean.</span>
              </p>

              <div className="w-full h-[24px]" />

              <p>
                <span>
                  You, on the other hand, have to live with REST endpoints mixed with gRPC, graphQL, random webhooks, a Rust rewrite that's always 90% done, and whatever MCP uses
                  as of this morning.
                </span>{" "}
                <span>
                  Your choices: litter your code with Otel spans (gross), grep logs hoping you logged the right thing (lol), or stare at Grafana wondering why your p99 spiked (very
                  helpful!).
                </span>
              </p>

              <div className="py-[24px] group">
                <div className="rotate-[-90deg] py-[64px] h-[72px] w-[72px] pr-auto relative grayscale-[0.60] opacity-[0.60]">
                  <div className="absolute rounded-[1px] left-[+0px] top-[36px] w-[8px] h-[8px] bg-zinc-400 rotate-[0deg] opacity-[0.60] ease-in-out transition-all duration-[125ms] delay-[750ms] group-hover:brightness-[1.10] group-hover:rotate-[0deg] group-hover:opacity-[1]" />
                  <div className="absolute rounded-[1px] left-[+16px] top-[46px] w-[8px] h-[8px] bg-[#dc2626] rotate-[-11deg] opacity-[0.60] ease-in-out transition-all duration-[125ms] delay-[600ms] group-hover:brightness-[1.10] group-hover:rotate-[+11deg] group-hover:opacity-[1]" />
                  <div className="absolute rounded-[1px] left-[+32px] top-[28px] w-[8px] h-[8px] bg-[#7c3aed] rotate-[+19deg] opacity-[0.60] ease-in-out transition-all duration-[125ms] delay-[450ms] group-hover:brightness-[1.10] group-hover:rotate-[-19deg] group-hover:opacity-[1]" />
                  <div className="absolute rounded-[1px] left-[+44px] top-[45px] w-[8px] h-[8px] bg-[#166534] rotate-[-23deg] opacity-[0.60] ease-in-out transition-all duration-[125ms] delay-[300ms] group-hover:brightness-[1.10] group-hover:rotate-[+23deg] group-hover:opacity-[1]" />
                  <div className="absolute rounded-[1px] left-[+60px] top-[36px] w-[8px] h-[8px] bg-[#1d4ed8] rotate-[+31deg] opacity-[0.60] ease-in-out transition-all duration-[125ms] delay-[150ms] group-hover:brightness-[1.10] group-hover:rotate-[-31deg] group-hover:opacity-[1]" />
                </div>
              </div>

              <p>We're building Subtrace to fix this.</p>

              <div className="w-full h-[24px]" />

              <p>
                <span>Today we added gRPC support to Subtrace.</span> <span>Open a terminal and do</span>{" "}
                <pre className="inline bg-zinc-800/90 outline outline-[1px] outline-zinc-700/90 w-fit px-[8px] py-[4px] mx-[4px] rounded-md font-mono text-[11px] leading-[22px]">
                  subtrace run -- ./app
                </pre>{" "}
                <span>to see every HTTP and gRPC call in your app with full payload.</span> <span>It’s like RPCStudio but works outside Mountain View.</span>
                <div className="relative group lg:p-2 lg:-m-2 lg:transition-all lg:delay-[75ms] lg:duration-[100ms] lg:ease-in-out lg:hover:brightness-[1.20]">
                  <aside className="w-fit lg:w-full max-w-[360px] lg:max-w-[min(100%,400px)] lg:absolute lg:left-[calc(100%+50px)] lg:bottom-[calc(100%+16px)] text-[13px] text-zinc-500/40 px-[32px] py-[20px] px-4 py-2 lg:py-0 rounded-lg border border-zinc-900 bg-zinc-900/25 lg:border-[0px] lg:bg-transparent my-8 lg:my-0">
                    <div className="lg:hidden text-zinc-600/50 font-mono text-[10px] mb-2 select-none">&lt;aside&gt;</div>
                    <span>Yes, even binary payloads. We wrote a</span>{" "}
                    <a
                      href="https://github.com/subtrace/subtrace/blob/6a6c195c21f56a2d73b646e204d189438cc2d117/tracer/parser.go#L86-L183"
                      className="border-b border-zinc-700/50 hover:border-zinc-700 px-[1px] mx-[-1px] pb-[1px] mb-[-1px] text-zinc-500/50 hover:brightness-[1.10]"
                      target="_blank"
                      rel="noreferrer nofollow"
                    >
                      recursive parser
                    </a>{" "}
                    <span>that converts protobufs to JSON on the fly (protobuf field names come later but field numbers work fine for now).</span>
                  </aside>
                  <div className="hidden lg:block transition-all duration-[100ms] ease-out absolute left-[calc(100%+60px)] group-hover:left-[calc(100%+45px)] bottom-[calc(100%+2px)] h-[10px] w-[30px] group-hover:w-[45px] rounded-br-lg border-r-[2px] border-b-[2px] border-zinc-800/70" />
                </div>
              </p>

              <div className="hidden md:block w-full h-[24px]" />

              <p>
                <span>Here's a quick video to show how it works:</span>
              </p>

              <div className="w-full h-[24px]" />

              <a
                href="https://www.youtube.com/watch?v=YvemlSjMQcs"
                className="w-full max-w-[360px] lg:max-w-[min(100%,400px)] p-[4px] bg-zinc-900/50 border border-zinc-700/50 rounded-[8px] flex space-x-4 items-center group hover:brightness-[1.20]"
                target="_blank"
              >
                <div className="w-[120px] aspect-[332/216] relative rounded-[7px] overflow-hidden border border-zinc-700/90 outline outline-[2px] outline-offset-[-3px] outline-black">
                  <img src={VideoThumbnail} className="w-full h-full" />
                  <lucide.Play
                    size={16}
                    strokeWidth={2.5}
                    className="opacity-[0.80] group-hover:opacity-[1] transition-opacity duration-[100ms] ease-in-out absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] text-zinc-300"
                  />
                </div>
                <div className="w-full grow flex flex-col text-[12px] text-zinc-300 space-y-1">
                  <span className="font-medium">gRPC debugging with Subtrace demo</span>
                  <span className="opacity-[0.25] font-mono text-[11px] font-medium tracking-tighter">1:27</span>
                </div>
                <div className="flex items-center justify-end ml-auto">
                  <lucide.ChevronRight size={24} strokeWidth={3} className="opacity-[0] group-hover:opacity-[1] pr-2 transition-all duration-[125ms] ease-in-out text-zinc-700" />
                </div>
              </a>

              <div className="w-full h-[24px]" />

              <p>
                <span>To do this without code changes or root privileges, we use BPF to intercept at the TCP socket layer and parse protocols on the fly.</span>{" "}
                <span>It adds &lt;100μs perf overhead and has a one command setup that takes less time than it took you to read this post.</span>{" "}
                <span>All languages are supported out of the box, even ones that haven't been invented yet!</span>
              </p>

              <div className="w-full h-[24px]" />

              <p>
                <span>The endgame isn't just HTTP and gRPC though.</span> <span>We want to be the Universal Network Debugger&trade; for your whole stack.</span>{" "}
                <span>Imagine debugging a weird bug where your gRPC server triggers an async HTTP webhook that queries Postgres before reading from S3 and you can see the</span>{" "}
                <i>entire flow</i> <span>with actual payloads + timings with zero manual logging.</span>
              </p>

              <div className="w-full h-[24px]" />

              <p>
                <span>That's the world I want to live in.</span>
              </p>

              <div className="w-full h-[24px]" />

              <p>
                <span>P.S. We're</span>{" "}
                <a
                  href="https://github.com/subtrace/subtrace"
                  className="border-b-[2px] border-zinc-600/50 hover:border-zinc-600 px-[1px] mx-[-1px] pb-[1px] mb-[-1px] text-zinc-200 hover:brightness-[1.10]"
                  target="_blank"
                >
                  open source
                </a>
                <span> + 2.7K stars + </span>
                <a
                  href="https://news.ycombinator.com/item?id=43096477"
                  className="border-b-[2px] border-zinc-600/50 hover:border-zinc-600 px-[1px] mx-[-1px] pb-[1px] mb-[-1px] text-zinc-200 hover:brightness-[1.10]"
                  target="_blank"
                >
                  HackerNews approved
                </a>{" "}
                <span>:)</span>
                <div className="relative group lg:p-2 lg:-m-2 lg:transition-all lg:delay-[75ms] lg:duration-[100ms] lg:ease-in-out lg:hover:brightness-[1.20]">
                  <aside className="w-fit lg:w-full max-w-[360px] lg:max-w-[min(100%,400px)] lg:absolute lg:left-[calc(100%+50px)] lg:bottom-[calc(100%+16px)] text-[13px] text-zinc-500/40 px-[32px] py-[20px] px-4 py-2 lg:py-0 rounded-lg border border-zinc-900 bg-zinc-900/25 lg:border-[0px] lg:bg-transparent my-8 lg:my-0">
                    <div className="lg:hidden text-zinc-600/50 font-mono text-[10px] mb-2 select-none">&lt;aside&gt;</div>
                    <span>The enterprise plan costs money but our</span>{" "}
                    <a href="/pricing" className="border-b border-zinc-700/50 hover:border-zinc-700 px-[1px] mx-[-1px] pb-[1px] mb-[-1px] text-zinc-500/50 hover:brightness-[1.10]">
                      free tier
                    </a>{" "}
                    <span>is generous.</span>
                  </aside>
                  <div className="hidden lg:block transition-all duration-[100ms] ease-out absolute left-[calc(100%+60px)] group-hover:left-[calc(100%+45px)] bottom-[calc(100%+2px)] h-[10px] w-[30px] group-hover:w-[45px] rounded-br-lg border-r-[2px] border-b-[2px] border-zinc-800/70" />
                </div>
              </p>
            </div>

            <div className="w-full h-[48px]" />

            <div className="w-full flex justify-begin">
              <div className="w-full max-w-[450px] flex space-x-4">
                <div className="aspect-[2/1] w-full max-w-lg flex flex-col justify-center items-center p-8 sm:p-16 rounded-lg border border-zinc-700/50 space-y-6 md:space-y-8 overflow-hidden relative">
                  <div className="text-[14px] sm:text-[17px] md:text-[20px] font-semibold flex items-center text-zinc-300">Debug faster with Subtrace</div>
                  <a
                    href="/login"
                    className="active:scale-[0.95] max-w-fit whitespace-pre group/button px-6 py-2 flex justify-center items-center space-x-2 rounded w-full text-sm font-medium bg-zinc-100 hover:brightness-[0.90] cursor-pointer text-zinc-700"
                  >
                    <div>Try for free</div>
                    <div className="duration-[100ms] translate-x-[-1px] group-hover/button:translate-x-[+1px]">&rarr;</div>
                  </a>
                  <div
                    className="z-[-5] opacity-[0.6] absolute top-[-100%] left-[-100%] w-[300%] h-[300%] animate-[bg-move_12s_linear_infinite]"
                    style={{
                      background: "radial-gradient(#27272a 25%, transparent 25%), radial-gradient(#27272a 25%, transparent 25%)",
                      backgroundPosition: "0px 0px, 96px 96px",
                      backgroundSize: "192px 192px",
                      backgroundColor: "#18181b",
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="w-full h-[96px]" />
          </div>
        </div>
      </div>
    </article>
  );
};
